import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { loadGoogleAds, revokeGoogleAds } from "./ads";

const CONSENT_KEY = "fbv_consent_v2";
const LEGACY_KEY = "fbv_consent_v1";
const SID_KEY = "fbv_sid_v1";
const INTERN_KEY = "kmg_intern_v1";

/**
 * Interne Geräte (Team) nie in der Statistik (Kevin 28.10.2026): Flag im Browser, gesetzt bei jeder Admin-/Mitarbeiter-Anmeldung
 * oder per Link ?intern=1 (Aufheben: ?intern=0). Bleibt auch nach dem Abmelden bestehen. IP-Filter geht nicht, PostHog anonymisiert IPs.
 */
export function isInternalDevice() {
  try {
    const q = new URLSearchParams(window.location.search).get("intern");
    if (q === "1") localStorage.setItem(INTERN_KEY, "1");
    if (q === "0") localStorage.removeItem(INTERN_KEY);
    // Wer Admin, Werkstatt oder Kreul Live öffnet, gehört zum Team (auch ohne Anmeldung in diesem Browser)
    if (/^\/(admin|werkstatt|live|einkauf)(\/|$)/.test(window.location.pathname)) localStorage.setItem(INTERN_KEY, "1");
    return localStorage.getItem(INTERN_KEY) === "1";
  } catch { return false; }
}

export function markInternalDevice() {
  try { localStorage.setItem(INTERN_KEY, "1"); } catch { /* noop */ }
  try { window.posthog?.stopSessionRecording?.(); window.posthog?.opt_out_capturing?.(); } catch { /* noop */ }
}

const ConsentCtx = createContext(null);
/** Erhöhen, wenn sich der Zweck von „Marketing“ ändert (dann fragt der Banner neu). 1 = Google Ads Conversion-Tracking (03.11.2026). */
const MARKETING_VERSION = 1;

function newSid() { return "sid_" + Math.random().toString(36).slice(2) + Date.now().toString(36); }

/**
 * consent shape: { necessary: true, analytics: bool, marketing: bool, at: ISO }
 * `necessary` ist immer true (Warenkorb, Auth, CSRF, Art. 6(1)f DSGVO / § 25(2) TDDDG).
 */
function loadConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      return c && c.mv === MARKETING_VERSION ? c : null;  // 03.11.2026: Marketing = Google Ads, ältere Zustimmungen neu einholen
    }
    // Legacy-Migration (nur analytics-boolean)
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const l = JSON.parse(legacy);
      return { necessary: true, analytics: !!l?.analytics, marketing: false, at: l?.at || new Date().toISOString() };
    }
  } catch { /* noop */ }
  return null;
}

/** Optionale Third-Party-Scripts nach Consent laden. Aktuell: PostHog. */
function applyThirdPartyScripts(consent) {
  if (typeof window === "undefined") return;
  // PostHog-Bibliothek wird erst nach Zustimmung geladen (window.__loadPosthog in index.html).
  if (consent?.analytics && !isInternalDevice()) { try { window.__loadPosthog?.(); } catch { /* noop */ } }
  if (consent?.marketing && !isInternalDevice()) loadGoogleAds(); else revokeGoogleAds();
  const ph = window.posthog;
  if (!ph || typeof ph.opt_in_capturing !== "function") return;
  try {
    if (consent?.analytics && !isInternalDevice()) {
      ph.opt_in_capturing();
      ph.startSessionRecording?.();  // Session Replay nur nach Zustimmung (Eingabefelder maskiert)
    } else {
      ph.stopSessionRecording?.();
      ph.opt_out_capturing?.();
    }
  } catch { /* noop */ }
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(loadConsent);
  // TDDDG § 25 (01.11.2026): keine Kennung ungefragt im Browser speichern, nur im Arbeitsspeicher
  const [sid] = useState(() => { try { localStorage.removeItem(SID_KEY); } catch { /* noop */ } return newSid(); });
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Beim Start: Consent-Status auf Third-Party-Scripts anwenden.
  useEffect(() => { applyThirdPartyScripts(consent); }, [consent]);

  const persist = (c) => { localStorage.setItem(CONSENT_KEY, JSON.stringify(c)); setConsent(c); };

  /** Kompatibilität: alter setDecision(boolean) → analytics an/aus, marketing = analytics. */
  const setDecision = (analytics, marketing = false) => {
    persist({ necessary: true, analytics: !!analytics, marketing: !!marketing, mv: MARKETING_VERSION, at: new Date().toISOString() });
  };
  const acceptAll = () => setDecision(true, true);
  const rejectAll = () => setDecision(false, false);
  const openSettings = () => setSettingsOpen(true);
  const closeSettings = () => setSettingsOpen(false);

  /** Shop-Ereignisse (Produkt angesehen, Warenkorb, Checkout, Bestellung) gehen an PostHog, nur nach Zustimmung. */
  const track = useCallback((eventType, eventData = {}) => {
    if (!consent?.analytics || isInternalDevice()) return;
    try { window.posthog?.capture?.(eventType, { ...eventData, path: window.location.pathname }); } catch { /* noop */ }
  }, [consent]);

  return (
    <ConsentCtx.Provider value={{ consent, setDecision, acceptAll, rejectAll, openSettings, closeSettings, settingsOpen, track, sid }}>
      {children}
    </ConsentCtx.Provider>
  );
}

export function useConsent() {
  const c = useContext(ConsentCtx);
  if (!c) throw new Error("useConsent outside provider");
  return c;
}

/** Erst-Banner mit 3 gleichgewichteten Aktionen (Alle akzeptieren / Alle ablehnen / Einstellungen). */
export function ConsentBanner() {
  const { consent, acceptAll, rejectAll, openSettings, settingsOpen } = useConsent();
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const en = path === "/en" || path.startsWith("/en/");
  const nl = path === "/nl" || path.startsWith("/nl/"); // Niederländischer Bereich
  if (consent && !settingsOpen) return null;
  return (
    <>
      {!consent && !settingsOpen && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:max-w-md z-[70] bg-white border border-hair shadow-2xl rounded-xl p-5" data-testid="consent-banner">
          <div className="font-heading font-semibold text-ink mb-2">{nl ? "Cookie-instellingen" : en ? "Cookie settings" : "Cookie-Einstellungen"}</div>
          {nl ? (
            <p className="text-sm text-ink-soft leading-relaxed mb-4">
              Wij gebruiken technisch noodzakelijke cookies voor winkelwagen, inloggen en beveiliging. Met uw toestemming
              verzamelen wij daarnaast anonieme statistieken om onze website te verbeteren en meten wij of onze Google-advertenties tot aankopen of aanvragen leiden (Google Ads).
              Details in ons <a href="/datenschutz" className="underline hover:text-brand">privacybeleid (Duits)</a>.
            </p>
          ) : en ? (
            <p className="text-sm text-ink-soft leading-relaxed mb-4">
              We use technically necessary cookies for the shopping cart, login and security. With your consent we also
              collect anonymous statistics to improve our website and measure whether our Google ads lead to purchases or enquiries (Google Ads).
              Details in our <a href="/datenschutz" className="underline hover:text-brand">privacy policy (German)</a>.
            </p>
          ) : (
          <p className="text-sm text-ink-soft leading-relaxed mb-4">
            Wir setzen technisch notwendige Cookies für Warenkorb, Anmeldung und
            Sicherheit. Zusätzlich möchten wir, mit Ihrer Zustimmung, anonyme
            Statistiken zur Verbesserung unseres Angebots erheben und messen, ob unsere
            Google-Anzeigen zu Käufen oder Anfragen führen (Google Ads). Details in unserer <a href="/datenschutz" className="underline hover:text-brand">Datenschutzerklärung</a>.
          </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button type="button" onClick={acceptAll} data-testid="consent-accept" className="btn-accent justify-center text-sm py-2">{nl ? "Alles accepteren" : en ? "Accept all" : "Alle akzeptieren"}</button>
            <button type="button" onClick={rejectAll} data-testid="consent-decline" className="btn-secondary justify-center text-sm py-2">{nl ? "Alleen noodzakelijke" : en ? "Necessary only" : "Nur notwendige"}</button>
            <button type="button" onClick={openSettings} data-testid="consent-settings" className="btn-secondary justify-center text-sm py-2">{nl ? "Instellingen" : en ? "Settings" : "Einstellungen"}</button>
          </div>
        </div>
      )}
      {settingsOpen && <ConsentSettings />}
    </>
  );
}

const SETTINGS_TXT = {
  de: { title: "Cookie-Einstellungen", hint: "Sie können Ihre Auswahl jederzeit über den Link „Cookie-Einstellungen“ im Footer ändern.",
    nec: "Notwendig", necText: "Erforderlich für Warenkorb, Anmeldung, Sicherheit und Cookie-Einstellung selbst.", necAria: "Notwendige Cookies (immer aktiv)",
    stat: "Statistik / Analyse", statText: "Anonymisierte Reichweiten-Messung (PostHog), nur nach Ihrer Zustimmung.",
    mkt: "Marketing", mktText: "Google Ads Conversion-Tracking: misst, ob ein Klick auf unsere Google-Anzeige zu einem Kauf oder einer Anfrage führt (Google Ireland Ltd.). Nur nach Ihrer Zustimmung.", save: "Auswahl speichern", cancel: "Abbrechen" },
  en: { title: "Cookie settings", hint: "You can change your choice at any time via the “Cookie settings” link in the footer.",
    nec: "Necessary", necText: "Required for the shopping cart, login, security and the cookie setting itself.", necAria: "Necessary cookies (always active)",
    stat: "Statistics / analytics", statText: "Anonymised audience measurement (PostHog), only with your consent.",
    mkt: "Marketing", mktText: "Google Ads conversion tracking: measures whether a click on our Google ad leads to a purchase or enquiry (Google Ireland Ltd.). Only with your consent.", save: "Save selection", cancel: "Cancel" },
  nl: { title: "Cookie-instellingen", hint: "U kunt uw keuze altijd wijzigen via de link “Cookie-instellingen” in de footer.",
    nec: "Noodzakelijk", necText: "Nodig voor winkelwagen, inloggen, beveiliging en de cookie-instelling zelf.", necAria: "Noodzakelijke cookies (altijd actief)",
    stat: "Statistiek / analyse", statText: "Geanonimiseerde bereikmeting (PostHog), alleen met uw toestemming.",
    mkt: "Marketing", mktText: "Google Ads-conversietracking: meet of een klik op onze Google-advertentie tot een aankoop of aanvraag leidt (Google Ireland Ltd.). Alleen met uw toestemming.", save: "Keuze opslaan", cancel: "Annuleren" },
};

/** Detailliertes Einstellungs-Modal. */
export function ConsentSettings() {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const T = SETTINGS_TXT[path === "/en" || path.startsWith("/en/") ? "en" : path === "/nl" || path.startsWith("/nl/") ? "nl" : "de"];
  const { consent, setDecision, closeSettings } = useConsent();
  const [analytics, setAnalytics] = useState(!!consent?.analytics);
  const [marketing, setMarketing] = useState(!!consent?.marketing);
  const save = () => { setDecision(analytics, marketing); closeSettings(); };
  return (
    <div className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="consent-settings-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="font-heading text-2xl text-ink mb-1">{T.title}</div>
        <p className="text-sm text-ink-soft mb-5">{T.hint}</p>

        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4 border border-hair rounded-lg px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">{T.nec}</div>
              <div className="text-xs text-ink-soft">{T.necText}</div>
            </div>
            <input type="checkbox" checked disabled className="mt-1" aria-label={T.necAria} />
          </div>
          <label className="flex items-start justify-between gap-4 border border-hair rounded-lg px-4 py-3 cursor-pointer">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">{T.stat}</div>
              <div className="text-xs text-ink-soft">{T.statText}</div>
            </div>
            <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} data-testid="consent-toggle-analytics" className="mt-1" />
          </label>
          <label className="flex items-start justify-between gap-4 border border-hair rounded-lg px-4 py-3 cursor-pointer">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">{T.mkt}</div>
              <div className="text-xs text-ink-soft">{T.mktText}</div>
            </div>
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="consent-toggle-marketing" className="mt-1" />
          </label>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <button type="button" onClick={save} data-testid="consent-save" className="btn-accent flex-1 justify-center text-sm py-2">{T.save}</button>
          <button type="button" onClick={closeSettings} className="btn-secondary flex-1 justify-center text-sm py-2">{T.cancel}</button>
        </div>
      </div>
    </div>
  );
}
